import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { HospitalStatus } from '@ics-referral-hub/shared-types';
import { AuthStore } from '../services/auth.service';
import { TokenService } from '../services/token.service';
import { JwtService } from '../services/jwt.service';
import { SessionActivityService } from '../services/session-activity.service';
import { SessionLogoutBridgeService } from '../services/session-logout-bridge.service';
import { SESSION_WARNING_MS, SESSION_REFRESH_BUFFER_MS } from '../../config/session.config';
import { environment } from '../../../environments/environment';
import { apiConfig } from '../../utils/api-config.util';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom, of, Subscription, timer } from 'rxjs';
import { switchMap, catchError, take, timeout } from 'rxjs/operators';
import { SessionTimeoutReason } from '../models/session-timeout-reason';
import { Logger } from '../../shared/utils/logger.util';
import { formatCountdown, countdownPercent } from '../utils/session-countdown.util';
import {
  CrossTabSessionChannel,
  withRefreshLock,
} from '../utils/cross-tab-session.util';

// State interface
interface SessionState {
  // Session status
  isAuthenticated: boolean;
  isRefreshing: boolean;
  showSessionWarning: boolean;

  // Timing
  sessionExpiryTime: number | null;
  // Absolute timestamp (ms) when the inactivity logout will occur.
  // Set when the warning fires; drives the modal countdown.
  inactivityLogoutTime: number | null;
  // Incremented every second while the warning modal is open so countdown
  // computed signals re-evaluate (Date.now() alone is not a reactive dependency).
  tick: number;
  lastRefreshAttempt: number;
  refreshFailureCount: number;

  // Error handling
  errorMessage: string | null;
}

// Initial state
const initialState: SessionState = {
  isAuthenticated: false,
  isRefreshing: false,
  showSessionWarning: false,
  sessionExpiryTime: null,
  inactivityLogoutTime: null,
  tick: 0,
  lastRefreshAttempt: 0,
  refreshFailureCount: 0,
  errorMessage: null,
};

// Timer configuration — JWT refresh only; inactivity warning/logout is handled by SessionActivityService
const TIMER_CONFIG = {
  TOKEN_REFRESH_BUFFER: SESSION_REFRESH_BUFFER_MS,
  REFRESH_RATE_LIMIT: 5000,
  MAX_REFRESH_ATTEMPTS: 3,
  REFRESH_RETRY_DELAY: 10_000,
  // Keep a safety margin below upstream IdP/Keycloak idle limits so active users
  // do not get logged out if the browser delays a timer slightly.
  MAX_REFRESH_INTERVAL: 8 * 60 * 1000,
  // Multi-tab race recovery: when a refresh returns 401, wait briefly and
  // retry once. If another tab just rotated the refresh cookie, the retry
  // will use the fresh cookie value and succeed. Only if the retry also
  // fails do we force a logout.
  REFRESH_401_RETRY_DELAY: 1500,
} as const;

// Shared (cross-tab) localStorage key written when a session is terminated
// (inactivity, token-expired, refresh-failed, manual). Writing it fires a
// `storage` event in awake peer tabs, and its persisted value lets a tab that
// was frozen/discarded (and missed the live BroadcastChannel + storage events)
// detect the logout when it regains visibility. Value is the termination
// timestamp (ms) so a tab only acts on a marker newer than its own session.
const SESSION_TERMINATED_KEY = 'ics_session_terminated';

export const SessionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed((store) => ({
    // Remaining time until the JWT expires (used only for refresh scheduling)
    timeUntilExpiry: computed(() => {
      const expiryTime = store.sessionExpiryTime();
      if (!expiryTime) return 0;
      return Math.max(0, expiryTime - Date.now());
    }),

    // True while the inactivity warning modal is showing and hasn't timed out
    isSessionExpiringSoon: computed(() => {
      store.tick();
      const logoutTime = store.inactivityLogoutTime();
      return !!logoutTime && logoutTime > Date.now();
    }),

    // Countdown shown in the warning modal — inactivity-based, not JWT-based
    timeRemainingDisplay: computed(() => {
      store.tick();
      return formatCountdown(store.inactivityLogoutTime());
    }),

    // Progress bar: fraction of the warning window remaining (0–100)
    progressPercent: computed(() => {
      store.tick();
      return countdownPercent(store.inactivityLogoutTime(), SESSION_WARNING_MS);
    }),
  })),

  withMethods((
    store,
    http = inject(HttpClient),
    router = inject(Router),
    tokenService = inject(TokenService),
    jwtService = inject(JwtService),
    sessionActivityService = inject(SessionActivityService),
    sessionLogoutBridge = inject(SessionLogoutBridgeService),
    authStore = inject(AuthStore),
    crossTabChannel = inject(CrossTabSessionChannel),
    queryClient = inject(QueryClient),
  ) => {
    let refreshTimerSubscription: Subscription | null = null;
    let expirationTimerSubscription: Subscription | null = null;
    let currentRefreshPromise: Promise<boolean> | null = null;
    let unsubscribeCrossTab: (() => void) | null = null;
    let visibilityChangeHandler: (() => void) | null = null;
    let storageLogoutHandler: ((event: StorageEvent) => void) | null = null;
    let countdownInterval: ReturnType<typeof setInterval> | null = null;
    // Timestamp (ms) when this tab's authenticated session began. Used to ignore
    // a stale cross-tab termination marker left over from a previous session so a
    // freshly logged-in tab is never immediately signed back out.
    let sessionStartedAt = 0;
    // Read the cross-tab termination marker, returning its timestamp if it is
    // strictly newer than this tab's session start (i.e. a logout that should
    // apply here). Strict `>` so a marker written in the same millisecond as
    // this tab's own login never signs the fresh session straight back out;
    // real timeouts always occur well after login.
    const peerTerminationAt = (): number | null => {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(SESSION_TERMINATED_KEY);
      if (!raw) return null;
      const at = Number(raw);
      return Number.isFinite(at) && at > sessionStartedAt ? at : null;
    };
    const clearTimerSubscriptions = () => {
      refreshTimerSubscription?.unsubscribe();
      expirationTimerSubscription?.unsubscribe();
      refreshTimerSubscription = null;
      expirationTimerSubscription = null;
    };

    return {

    // Initialize session management
    initializeSession(): void {
      Logger.log('SessionStore: initializeSession called');
      this.setupCrossTabCoordination();
      this.syncWithTokenService();
    },

    // Setup cross-tab refresh coordination + tab-visibility refresh trigger.
    // Idempotent: safe to call multiple times.
    setupCrossTabCoordination(): void {
      if (!unsubscribeCrossTab) {
        unsubscribeCrossTab = crossTabChannel.subscribe((msg) => {
          if (msg.type === 'token-refreshed' && msg.accessToken) {
            const existing = tokenService.getAccessTokenValue();
            if (existing === msg.accessToken) return;
            Logger.log(
              'SessionStore: received refreshed token from peer tab, adopting'
            );
            tokenService.setAccessToken(msg.accessToken);
            authStore.updateAccessToken(
              msg.accessToken,
              msg.expiresIn,
              msg.hospitalStatus,
            );
            // Leave showSessionWarning alone: the warning is inactivity-based,
            // and a peer tab's refresh says nothing about this tab's idleness
            // (MYB-5195).
            patchState(store, {
              refreshFailureCount: 0,
              isRefreshing: false,
              lastRefreshAttempt: Date.now(),
            });
            this.setupSessionTimers(msg.accessToken);
          } else if (msg.type === 'session-logout') {
            this.applyPeerLogout();
          }
        });
      }
      if (
        !visibilityChangeHandler &&
        typeof document !== 'undefined' &&
        typeof document.addEventListener === 'function'
      ) {
        visibilityChangeHandler = () => {
          if (document.visibilityState !== 'visible') return;
          if (!store.isAuthenticated()) return;
          // A tab that was frozen/discarded while hidden may have missed both
          // the live BroadcastChannel message and the `storage` event for a
          // session terminated in another tab. On regaining visibility, honour
          // the persisted termination marker before doing anything else.
          if (peerTerminationAt() !== null) {
            Logger.log(
              'SessionStore: tab visible + peer-terminated marker present, logging out'
            );
            this.applyPeerLogout();
            return;
          }
          const expiryTime = store.sessionExpiryTime();
          if (!expiryTime) return;
          const timeUntilExpiry = expiryTime - Date.now();
          // If the token is already within the refresh buffer window (or
          // past it) when the tab comes back into focus, proactively
          // refresh — background tab timer throttling may have skipped our
          // scheduled refresh.
          if (timeUntilExpiry <= TIMER_CONFIG.TOKEN_REFRESH_BUFFER) {
            Logger.log(
              'SessionStore: tab visible + token near expiry, refreshing'
            );
            this.refreshToken();
          }
        };
        document.addEventListener('visibilitychange', visibilityChangeHandler);
      }
      // Cross-tab logout fallback: `storage` events reach awake peer tabs even
      // when a BroadcastChannel message is missed, and reliably wake same-origin
      // tabs that BroadcastChannel alone does not. Fires when another tab writes
      // the termination marker in handleSessionTimeout().
      if (
        !storageLogoutHandler &&
        typeof window !== 'undefined' &&
        typeof window.addEventListener === 'function'
      ) {
        storageLogoutHandler = (event: StorageEvent) => {
          if (event.key !== SESSION_TERMINATED_KEY || !event.newValue) return;
          if (!store.isAuthenticated()) return;
          if (peerTerminationAt() === null) return;
          Logger.log(
            'SessionStore: peer tab termination marker written, clearing local session'
          );
          this.applyPeerLogout();
        };
        window.addEventListener('storage', storageLogoutHandler);
      }
    },

    // Clear local session in response to a logout that occurred in another tab.
    // Mirrors handleSessionTimeout's local cleanup + navigation, but does NOT
    // re-broadcast or re-write the marker (the originating tab already did) and
    // does not call the backend logout (the cookie is already invalidated).
    applyPeerLogout(): void {
      if (!store.isAuthenticated()) return;
      Logger.log('SessionStore: applying peer-initiated logout');
      this.clearSession();
      sessionLogoutBridge.notify('manual');
      localStorage.removeItem('authState');
      // A tab left open across a deploy may have a stale in-memory bundle whose
      // lazy 'auth' chunk no longer exists on the server, rejecting navigate()
      // (ChunkLoadError). Without a fallback the tab is left stuck on the
      // now-unauthenticated page it was already on. Hard-redirect so the user
      // always ends up on a fresh, correctly-served login page.
      router.navigate(['/auth/login'], {}).then((ok) => {
        if (!ok) window.location.href = '/auth/login';
      }).catch(() => {
        window.location.href = '/auth/login';
      });
    },

    // Sync with token service
    syncWithTokenService(): void {
      const token = tokenService.getAccessTokenValue();
      const wasAuthenticated = store.isAuthenticated();
      const isNowAuthenticated = !!token;

      Logger.log('SessionStore: syncWithTokenService', {
        token: !!token,
        wasAuthenticated,
        isNowAuthenticated,
        tokenLength: token?.length
      });

      if (isNowAuthenticated && !wasAuthenticated) {
        // User just logged in - set up session.
        // Drop anything the previous account left in memory before this
        // session's pages can paint it (MYB-5212).
        queryClient.clear();
        Logger.log('SessionStore: User just logged in, setting up timers');
        // Stamp this session's start and clear any termination marker left by a
        // previous session so this fresh login is not immediately signed out by
        // its own (now-stale) cross-tab logout marker.
        sessionStartedAt = Date.now();
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(SESSION_TERMINATED_KEY);
          }
        } catch {
          // ignore
        }
        patchState(store, { isAuthenticated: true, lastRefreshAttempt: Date.now() });
        this.setupCrossTabCoordination();
        this.setupSessionTimers(token);
        this.startActivityMonitoring();
      } else if (isNowAuthenticated && wasAuthenticated) {
        // Already authenticated — nothing extra needed
        Logger.log('SessionStore: User already authenticated');
      } else if (!isNowAuthenticated && wasAuthenticated) {
        Logger.log('SessionStore: User was logged out, clearing session');
        // User was logged out
        this.clearSession();
      }
    },

    // Setup session timers using RxJS
    setupSessionTimers(token: string): void {
      try {
        Logger.log('SessionStore: setupSessionTimers called');
        clearTimerSubscriptions();
        const payload = jwtService.decodeToken(token);
        if (!payload?.exp) {
          Logger.error('SessionStore: Invalid token payload');
          this.handleSessionError('Invalid token payload');
          return;
        }

        const tokenExpiryTime = payload.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = tokenExpiryTime - currentTime;
        const issuedAt = payload.iat ? payload.iat * 1000 : 'unknown';
        const tokenLifetime = issuedAt !== 'unknown' ? tokenExpiryTime - issuedAt : 'unknown';

        Logger.log('SessionStore: Token analysis', {
          tokenExpiryTime: new Date(tokenExpiryTime).toISOString(),
          currentTime: new Date(currentTime).toISOString(),
          timeUntilExpiry: Math.round(timeUntilExpiry / 1000) + ' seconds',
          refreshBuffer: Math.round(TIMER_CONFIG.TOKEN_REFRESH_BUFFER / 1000) + ' seconds',
          issuedAt: issuedAt !== 'unknown' ? new Date(issuedAt).toISOString() : 'unknown',
          tokenLifetime: tokenLifetime !== 'unknown' ? Math.round(tokenLifetime / 1000) + ' seconds' : 'unknown',
          iat: payload.iat,
          exp: payload.exp
        });

        if (timeUntilExpiry <= 0) {
          Logger.log('SessionStore: Token already expired');
          this.handleSessionTimeout('token-expired');
          return;
        }

        patchState(store, { sessionExpiryTime: tokenExpiryTime });

        // Set up refresh timer using RxJS
        let refreshTime = timeUntilExpiry - TIMER_CONFIG.TOKEN_REFRESH_BUFFER;
        Logger.log('SessionStore: Initial refresh time calculation', Math.round(refreshTime / 1000) + ' seconds');

        // To prevent Keycloak/IdP idle timeouts from expiring our session before we
        // attempt to refresh, cap the maximum time between refreshes with headroom.
        if (refreshTime > TIMER_CONFIG.MAX_REFRESH_INTERVAL) {
          refreshTime = TIMER_CONFIG.MAX_REFRESH_INTERVAL;
          Logger.log('SessionStore: Refresh time capped to prevent Keycloak idle timeout:', Math.round(refreshTime / 1000) + ' seconds');
        }

        if (refreshTime <= 0) {
          // If refresh buffer is larger than token lifetime, refresh at half the token lifetime
          refreshTime = timeUntilExpiry / 2;
          Logger.log('SessionStore: Refresh buffer too large, using half token lifetime:', Math.round(refreshTime / 1000) + ' seconds');
        }

        Logger.log('SessionStore: Setting refresh timer for', Math.round(refreshTime / 1000) + ' seconds');
        refreshTimerSubscription = timer(refreshTime).pipe(
          take(1),
          switchMap(() => {
            Logger.log('SessionStore: Refresh timer triggered!');
            return this.refreshToken();
          })
        ).subscribe({
          next: (refreshed) => {
            if (!refreshed) {
              // Refresh returned false (rate-limited or already refreshing).
              // Schedule a retry so the warning timer doesn't fire on a stale token.
              Logger.log('SessionStore: Refresh timer returned false, scheduling retry in',
                Math.round(TIMER_CONFIG.REFRESH_RETRY_DELAY / 1000) + 's');
              refreshTimerSubscription = timer(TIMER_CONFIG.REFRESH_RETRY_DELAY).pipe(
                take(1),
                switchMap(() => this.refreshToken())
              ).subscribe();
            }
          }
        });

        // Set up expiration timer as fallback — attempt a refresh before forcing logout.
        // refreshToken() can return false for benign reasons (rate-limited, an
        // in-flight refresh already running, or a single transient network error
        // still under its own MAX_REFRESH_ATTEMPTS grace period) — refreshToken()
        // itself is the sole authority on when a failure is real enough to log the
        // user out (immediate on a definitive 401, or after repeated failures).
        // So `false` here is not evidence of a genuine session expiry: retry once
        // before concluding the session is truly gone, and never force a logout
        // that refreshToken() hasn't already decided on (store.isAuthenticated()
        // already false means it made that call itself).
        Logger.log('SessionStore: Setting expiration timer for', Math.round(timeUntilExpiry / 1000) + ' seconds');
        const attemptExpiryRefresh = (isRetry: boolean) => {
          this.refreshToken()
            .then((refreshed) => {
              if (refreshed) {
                Logger.log('SessionStore: Refresh succeeded in expiration timer');
                return;
              }
              if (!store.isAuthenticated()) return;
              if (isRetry) {
                Logger.log('SessionStore: Refresh retry also failed in expiration timer, logging out');
                this.handleSessionTimeout('token-expired');
                return;
              }
              Logger.log('SessionStore: Refresh failed in expiration timer, retrying once before logging out');
              setTimeout(() => attemptExpiryRefresh(true), TIMER_CONFIG.REFRESH_RETRY_DELAY);
            })
            .catch(() => {
              if (!store.isAuthenticated()) return;
              if (isRetry) {
                Logger.log('SessionStore: Refresh error on retry in expiration timer, logging out');
                this.handleSessionTimeout('token-expired');
                return;
              }
              Logger.log('SessionStore: Refresh error in expiration timer, retrying once before logging out');
              setTimeout(() => attemptExpiryRefresh(true), TIMER_CONFIG.REFRESH_RETRY_DELAY);
            });
        };
        expirationTimerSubscription = timer(timeUntilExpiry).pipe(
          take(1)
        ).subscribe(() => {
          Logger.log('SessionStore: Expiration timer triggered, attempting refresh');
          attemptExpiryRefresh(false);
        });

      } catch (error) {
        Logger.error('Error setting up session timers:', error);
        this.handleSessionError('Timer setup failed');
      }
    },

    // Refresh token with reactive error handling
    async refreshToken(): Promise<boolean> {
      // Reuse the in-flight refresh so concurrent callers get the real result.
      if (currentRefreshPromise) {
        return currentRefreshPromise;
      }

      // Only proceed if we have a token to refresh
      const currentToken = tokenService.getAccessTokenValue();
      if (!currentToken) {
        return false;
      }

      // Simple rate limiting
      const now = Date.now();
      if (now - store.lastRefreshAttempt() < TIMER_CONFIG.REFRESH_RATE_LIMIT) {
        return false;
      }

      patchState(store, {
        isRefreshing: true,
        lastRefreshAttempt: now,
        errorMessage: null,
      });

      const sleep = (ms: number) =>
        new Promise<void>((resolve) => setTimeout(resolve, ms));

      // Single HTTP call to /auth/refresh-token. Returns the response body on
      // success, or throws the HttpErrorResponse on failure.
      const doRefreshRequest = () =>
        lastValueFrom(
          http.post<{
            access_token: string;
            expires_in: number;
            hospitalStatus?: HospitalStatus;
          }>(
            `${environment.apiUrl}${apiConfig.prefix}/auth/refresh-token`,
            {}
          )
        );

      const runRefresh = async (): Promise<boolean> => {
        try {
          // First attempt.
          let response: {
            access_token: string;
            expires_in: number;
            hospitalStatus?: HospitalStatus;
          };
          try {
            response = await doRefreshRequest();
          } catch (firstError) {
            // Multi-tab recovery: if we got a 401 and another tab may have
            // just rotated the refresh cookie, wait briefly and retry ONCE.
            // The browser will send the latest cookie value on the retry.
            if (
              firstError instanceof HttpErrorResponse &&
              firstError.status === 401
            ) {
              Logger.warn(
                'SessionStore: refresh returned 401, retrying once after',
                TIMER_CONFIG.REFRESH_401_RETRY_DELAY + 'ms (multi-tab race recovery)'
              );
              await sleep(TIMER_CONFIG.REFRESH_401_RETRY_DELAY);
              // If meanwhile another tab broadcast a fresh token, abort the
              // retry — we are already authenticated.
              const tokenAfterWait = tokenService.getAccessTokenValue();
              if (tokenAfterWait && tokenAfterWait !== currentToken) {
                Logger.log(
                  'SessionStore: peer tab provided fresh token during 401 retry wait; skipping retry'
                );
                patchState(store, {
                  refreshFailureCount: 0,
                  isRefreshing: false,
                });
                return true;
              }
              response = await doRefreshRequest();
            } else {
              throw firstError;
            }
          }

          // Success path (first attempt or retry).
          // Guard: if the session was intentionally terminated while this HTTP
          // request was in-flight (e.g. inactivity logout timer fired
          // concurrently with Extend Session), discard the response.
          // Applying the token here would silently ghost-re-authenticate the
          // user, leaving pendingLogoutReason in sessionStorage and causing
          // subsequent 401 errors to be permanently swallowed by the interceptor
          // (handleUnauthorizedRequest returns false, tokenAtError === currentToken
          // so the background-rotation fallback doesn't retry either).
          if (
            typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem('pendingLogoutReason')
          ) {
            patchState(store, { isRefreshing: false });
            return false;
          }
          tokenService.setAccessToken(response.access_token);
          authStore.updateAccessToken(
            response.access_token,
            response.expires_in,
            response.hospitalStatus,
          );
          // A token refresh must not hide the inactivity warning: scheduled
          // refreshes (every ≤8 min) land inside the 5-min warning window, and
          // hiding the modal without resetting the idle clock logged idle users
          // out with no warning on screen (MYB-5195). Only extendSession() and
          // dismissSessionWarning() close it.
          patchState(store, {
            refreshFailureCount: 0,
            isRefreshing: false,
          });
          Logger.log('Token refresh successful');
          this.setupSessionTimers(response.access_token);

          // Notify peer tabs so they can update their in-memory access token
          // without making their own refresh call (which would lose the
          // rotation race).
          crossTabChannel?.publish({
            type: 'token-refreshed',
            accessToken: response.access_token,
            expiresIn: response.expires_in,
            hospitalStatus: response.hospitalStatus,
            at: Date.now(),
          });

          return true;
        } catch (error) {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            // Both the initial call AND the retry returned 401 → refresh
            // token is genuinely no longer accepted. Force logout.
            Logger.warn(
              'SessionStore: refresh returned 401 after retry, ending session'
            );
            patchState(store, {
              isRefreshing: false,
              errorMessage: null,
            });
            this.handleSessionTimeout('token-expired');
            return false;
          }

          // Transient error (network, 5xx, 503 from our backend for upstream
          // Keycloak issues). Count failures but do NOT log the user out
          // immediately — the next scheduled refresh or the expiration
          // timer will retry.
          const newFailureCount = store.refreshFailureCount() + 1;
          patchState(store, {
            refreshFailureCount: newFailureCount,
            isRefreshing: false,
            errorMessage:
              newFailureCount > 1 ? 'Session refresh failed' : null,
          });

          if (newFailureCount >= TIMER_CONFIG.MAX_REFRESH_ATTEMPTS) {
            this.handleSessionTimeout('refresh-failed');
          }

          return false;
        }
      };

      // Serialize across tabs when the Web Locks API is available so only
      // one tab at a time hits the refresh endpoint and rotates the cookie.
      const refreshPromise = withRefreshLock(runRefresh).finally(() => {
        currentRefreshPromise = null;
      });

      currentRefreshPromise = refreshPromise;
      return refreshPromise;
    },

    /**
     * Make sure the access token has at least `minValidityMs` left before a
     * user-initiated save. A tab that slept (Edge sleeping tabs, a closed
     * laptop) wakes with an expired token because its refresh timer never ran;
     * refreshing first keeps Save Draft and Complete Form from going out with
     * it. Resolves true when a usable token is in hand.
     */
    async ensureFreshToken(minValidityMs = 60_000): Promise<boolean> {
      const validFor = (token: string | null): number => {
        if (!token) return 0;
        const exp = jwtService.decodeToken(token)?.exp;
        return exp ? exp * 1000 - Date.now() : 0;
      };

      if (validFor(tokenService.getAccessTokenValue()) > minValidityMs) return true;
      if (await this.refreshToken()) return true;
      // refreshToken() returns false when rate-limited, i.e. a refresh just
      // ran. That refresh may have succeeded, so check the token again.
      return validFor(tokenService.getAccessTokenValue()) > 0;
    },

    // Handle unauthorized requests
    async handleUnauthorizedRequest(): Promise<boolean> {
      // If the session was already terminated (inactivity, token-expired, etc.),
      // the logout navigation has already been triggered — don't attempt another
      // refresh against a cookie that has been invalidated on the backend.
      if (typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('pendingLogoutReason')) {
        return false;
      }
      return this.refreshToken();
    },

    // Wait for refresh completion
    async waitForRefresh(): Promise<boolean> {
      return currentRefreshPromise ?? false;
    },

    // Session timeout handling
    handleSessionTimeout(reason: SessionTimeoutReason): void {
      Logger.log(`Session timeout: ${reason}`);

      // Notify peer tabs first so they can clear their own session state
      // instead of each tab independently hitting a (now-invalid) refresh.
      // Two channels for reliability: the live BroadcastChannel (instant for
      // awake peers) and a persisted localStorage marker (fires a `storage`
      // event for awake peers, and survives so a frozen/discarded tab can
      // detect the logout when it regains visibility — see peerTerminationAt).
      const terminatedAt = Date.now();
      try {
        crossTabChannel?.publish({
          type: 'session-logout',
          at: terminatedAt,
        });
      } catch {
        // ignore
      }
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(SESSION_TERMINATED_KEY, String(terminatedAt));
        }
      } catch {
        // ignore
      }

      // Clear session state and notify auth store before redirecting
      this.clearSession();

      sessionLogoutBridge.notify(reason);

      // Clear legacy local storage key if it still exists
      localStorage.removeItem('authState');

      // Persist the logout reason before the async backend call so back-button
      // navigation during the request is still blocked by the auth guard.
      // Skip for manual logout — the user chose to leave, no error context needed.
      if (reason !== 'manual' && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('pendingLogoutReason', reason);
      }

      // Navigate to login with appropriate query parameters
      const queryParams: Record<string, string> = {};
      if (reason === 'inactivity') {
        queryParams['error'] = 'inactivity';
        queryParams['message'] = 'Your session has expired due to inactivity. Please log in again.';
      } else {
        queryParams['error'] = 'token_expired';
        queryParams['message'] = 'Your session has expired. For your security, please log in again to continue.';
      }

      // Wait for the backend to clear the HTTP-only refresh cookie before
      // navigating. The cookie can only be cleared server-side, so we must
      // receive the Set-Cookie response before the browser can send a back-button
      // refresh request. A 3 s timeout ensures the user is never stuck.
      http.post(`${environment.apiUrl}${apiConfig.prefix}/auth/logout`, {}).pipe(
        take(1),
        timeout(3000),
        catchError((err) => {
          Logger.warn('SessionStore: Backend logout failed (non-blocking):', err);
          return of(null);
        }),
      ).subscribe(() => {
        // Fall back to a hard redirect if navigate() fails (e.g. a tab left
        // open across a deploy trying to load a lazy chunk the server no
        // longer serves) — see applyPeerLogout for the same guard.
        router.navigate(['/auth/login'], { queryParams }).then((ok) => {
          if (!ok) window.location.href = '/auth/login';
        }).catch(() => {
          window.location.href = '/auth/login';
        });
      });
    },

    // Handle session errors
    handleSessionError(error: string): void {
      patchState(store, {
        errorMessage: error,
        isAuthenticated: false,
      });

      this.clearSession();
    },

    // Activity monitoring — warning and logout are inactivity-based, independent of JWT
    startActivityMonitoring(): void {
      if (store.isAuthenticated()) {
        sessionActivityService.startMonitoring(
          (remainingMs: number) => {
            // Inactivity warning threshold reached — show modal with true remaining time
            Logger.log('SessionStore: Inactivity warning fired, logout in',
              Math.round(remainingMs / 1000) + 's');
            patchState(store, {
              showSessionWarning: true,
              inactivityLogoutTime: Date.now() + remainingMs,
            });
            // Drive the countdown display: tick every second so computed signals re-evaluate
            if (countdownInterval) clearInterval(countdownInterval);
            countdownInterval = setInterval(() => patchState(store, { tick: Date.now() }), 1000);
          },
          () => {
            Logger.log('SessionStore: Inactivity logout fired');
            this.handleSessionTimeout('inactivity');
          },
          () => {
            // Throttled activity callback (~60s) — proactively refresh JWT if it is
            // within the refresh buffer window so active users never see a JWT error.
            const expiryTime = store.sessionExpiryTime();
            if (!expiryTime) return;
            const timeUntilExpiry = expiryTime - Date.now();
            const timeSinceLastRefresh = Date.now() - store.lastRefreshAttempt();
            
            if ((timeUntilExpiry > 0 && timeUntilExpiry <= TIMER_CONFIG.TOKEN_REFRESH_BUFFER) || 
                timeSinceLastRefresh >= TIMER_CONFIG.MAX_REFRESH_INTERVAL) {
              Logger.log('SessionStore: Proactive JWT refresh on activity', {
                timeUntilExpiry: Math.round(timeUntilExpiry / 1000) + 's',
                timeSinceLastRefresh: Math.round(timeSinceLastRefresh / 1000) + 's'
              });
              this.refreshToken();
            }
          }
        );
      }
    },

    stopActivityMonitoring(): void {
      sessionActivityService.stopMonitoring();
    },

    // Clear session
    clearSession(): void {
      // Every session end lands here: inactivity, refresh failure, peer-tab
      // logout, and the token being cleared. The list cache is keyed by
      // filters and page, not by user, so leave it and the next account
      // briefly sees the previous account's referrals (MYB-5212).
      queryClient.clear();
      clearTimerSubscriptions();
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      patchState(store, {
        isAuthenticated: false,
        isRefreshing: false,
        showSessionWarning: false,
        sessionExpiryTime: null,
        inactivityLogoutTime: null,
        lastRefreshAttempt: 0,
        errorMessage: null,
        refreshFailureCount: 0,
      });

      tokenService.clearTokens();
      this.stopActivityMonitoring();

      if (
        visibilityChangeHandler &&
        typeof document !== 'undefined' &&
        typeof document.removeEventListener === 'function'
      ) {
        try {
          document.removeEventListener('visibilitychange', visibilityChangeHandler);
        } catch {
          // ignore
        }
        visibilityChangeHandler = null;
      }
      if (
        storageLogoutHandler &&
        typeof window !== 'undefined' &&
        typeof window.removeEventListener === 'function'
      ) {
        try {
          window.removeEventListener('storage', storageLogoutHandler);
        } catch {
          // ignore
        }
        storageLogoutHandler = null;
      }
      // Keep crossTabChannel alive so a subsequent login on the same tab can
      // reuse it; just drop the listener to avoid acting on stale messages.
      unsubscribeCrossTab?.();
      unsubscribeCrossTab = null;
    },

    // User actions
    async extendSession(): Promise<void> {
      // Always dismiss the inactivity warning and reset the activity clock.
      // That is the primary purpose of "Extend Session". Token refresh is
      // best-effort: refreshToken() can return false for benign reasons
      // (5s rate-limit after a proactive/scheduled refresh, in-flight peer
      // refresh, etc.). Gating dismissal on refresh success left the modal
      // stuck open with no spinner when the user clicked shortly after a
      // background refresh.
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      sessionActivityService.resetActivity();
      patchState(store, {
        showSessionWarning: false,
        inactivityLogoutTime: null,
      });

      await this.refreshToken();
    },

    dismissSessionWarning(): void {
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      patchState(store, { showSessionWarning: false, inactivityLogoutTime: null });
    },

    logout(): void {
      this.handleSessionTimeout('manual');
    },

  };
}));
